import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessageWrappedInSpan } from '../../misc';
import { trackEvent } from '../../../utils/ga-tracking';
import { getAppLanguage } from '../../../intl/languages';

const MediaListInfoTile = ({
  participantMediaCount,
  onClick,
  type,
  isMainSearchUsed,
}) => {
  const [startAnimate, setStartAnimate] = useState(false);
  const animatedElementRef = useRef();

  const linkToFAQForalternativeSearchHint = (
    <a
      href={`https://helpdesk.sportograf.com/${getAppLanguage()}/support/solutions/articles/77000265399`}
      target="_blank"
      rel="noopener noreferrer"
      className="link-blue-underline"
    >
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.link"
        defaultMessage="Learn more"
      />
    </a>
  );

  const handleScroll = () => {
    if (
      animatedElementRef.current?.getBoundingClientRect().top +
        animatedElementRef.current?.parentNode?.offsetTop <=
      window.innerHeight + window.scrollY
    ) {
      setStartAnimate(true);
      window.removeEventListener('scroll', handleScroll);
    }
  };

  useEffect(() => {
    if (
      animatedElementRef.current?.getBoundingClientRect().top +
        animatedElementRef.current?.parentNode?.offsetTop <=
      window.innerHeight - 100
    ) {
      setStartAnimate(true);
    } else {
      window.addEventListener('scroll', handleScroll);
    }
  }, []);

  const morePhotosTexts = {
    header: (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.morePhotos.heading"
        defaultMessage="More Photos"
      />
    ),
    description: (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.morePhotos.description"
        defaultMessage="We found {count} personal photos, but you can search for more."
        values={{ count: participantMediaCount }}
      />
    ),
    buttonText: (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.extendedSearchHintButton"
        defaultMessage="+ Are there more photos of me?"
      />
    ),
  };

  const alternativeSearchHintTexts = {
    header: (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.heading"
        defaultMessage="How to buy?"
      />
    ),
    description: (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.descriptionWithLink"
        defaultMessage="You are in an alternative search. {link}"
        values={{
          link: linkToFAQForalternativeSearchHint,
        }}
      />
    ),
    additionalInstruction: isMainSearchUsed ? (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.descriptionMainSearchWasUsed"
        defaultMessage="Click below to go back to your personal Foto-Flat offer."
      />
    ) : (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.descriptionOnlyDualWasUsed"
        defaultMessage="To see your personal Foto-Flat offer, use our main search feature above."
      />
    ),
    buttonText: isMainSearchUsed ? (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.buttonToGoBackToPhotos"
        defaultMessage="Go to offer"
      />
    ) : (
      <FormattedMessageWrappedInSpan
        id="mediaListInfoTile.alternativeSearchHint.buttonGoToSearch"
        defaultMessage="Go to search"
      />
    ),
  };

  const defineTileTexts = () => {
    switch (type) {
      case 'morePhotos':
        return morePhotosTexts;

      case 'alternativeSearchHint':
        return alternativeSearchHintTexts;
    }
  };

  return (
    <div
      ref={animatedElementRef}
      className={classNames('more-info-tile more-info-tile-animated', {
        'more-info-tile-animated_start': startAnimate,
      })}
    >
      <div className="more-info-tile-header">{defineTileTexts().header}</div>
      <div className="more-info-tile-body">
        <div className="more-info-tile-item">{defineTileTexts().description}</div>
        {defineTileTexts().additionalInstruction && (
          <div className="more-info-tile-item">
            {defineTileTexts().additionalInstruction}
          </div>
        )}
      </div>
      <div
        className={classNames('more-info-tile-button', {
          'more-info-tile-button_small': type === 'morePhotos',
        })}
        onClick={() => {
          if (type === 'morePhotos') {
            trackEvent('Event', 'Search', 'Are there more photos of me box');
          }
          onClick();
        }}
      >
        <div>{defineTileTexts().buttonText}</div>
      </div>
    </div>
  );
};

MediaListInfoTile.propTypes = {
  participantMediaCount: PropTypes.number,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['morePhotos', 'alternativeSearchHint']),
  isMainSearchUsed: PropTypes.bool,
};

export default MediaListInfoTile;
